import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

gsap?.registerPlugin(ScrollTrigger);

const ROICalculator = () => {
  const [language, setLanguage] = useState('en');
  const [inputs, setInputs] = useState({
    monthlyBookings: 150,
    noShowRate: 25,
    avgBookingValue: 75,
    staffHours: 20,
  });
  const [results, setResults] = useState({});
  const [isCalculated, setIsCalculated] = useState(false);

  const sectionRef = useRef(null);
  const calculatorRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      gsap?.fromTo(
        calculatorRef?.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: calculatorRef?.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  useEffect(() => {
    if (isCalculated && resultsRef?.current) {
      gsap?.fromTo(
        resultsRef?.current,
        { scale: 0.9, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.8, ease: 'back.out(1.7)' }
      );
    }
  }, [isCalculated]);

  const content = {
    en: {
      title: 'Calculate Your ROI',
      subtitle:
        'See exactly how much time and money Slotyfi can save your business',
      form: {
        monthlyBookings: 'Monthly Bookings',
        noShowRate: 'Current No-Show Rate (%)',
        avgBookingValue: 'Average Booking Value ($)',
        staffHours: 'Weekly Hours on Scheduling',
      },
      calculate: 'Calculate Savings',
      results: {
        title: 'Your Potential Savings',
        subtitle: "Here's what Slotyfi could save you annually",
        metrics: {
          timeSaved: 'Hours Saved',
          revenueSaved: 'Revenue Recovered',
          costSaved: 'Cost Reduction',
          roiPercent: 'ROI Percentage',
        },
        breakdown: 'Savings Breakdown',
        getStarted: 'Start Saving Now',
      },
    },
    fa: {
      title: 'محاسبه بازگشت سرمایه شما',
      subtitle:
        'دقیقاً ببینید اسلاتیفای چقدر زمان و پول کسب‌وکار شما را صرفه‌جویی می‌کند',
      form: {
        monthlyBookings: 'رزروهای ماهانه',
        noShowRate: 'نرخ فعلی غیبت (%)',
        avgBookingValue: 'ارزش متوسط رزرو (دلار)',
        staffHours: 'ساعات هفتگی برنامه‌ریزی',
      },
      calculate: 'محاسبه صرفه‌جویی',
      results: {
        title: 'صرفه‌جویی‌های بالقوه شما',
        subtitle:
          'این چیزی است که اسلاتیفای می‌تواند سالانه برای شما صرفه‌جویی کند',
        metrics: {
          timeSaved: 'ساعات صرفه‌جویی شده',
          revenueSaved: 'درآمد بازیافت شده',
          costSaved: 'کاهش هزینه',
          roiPercent: 'درصد بازگشت سرمایه',
        },
        breakdown: 'تفکیک صرفه‌جویی‌ها',
        getStarted: 'شروع صرفه‌جویی کنید',
      },
    },
  };

  const currentContent = content?.[language];

  const calculateROI = () => {
    const { monthlyBookings, noShowRate, avgBookingValue, staffHours } = inputs;

    // Current losses
    const monthlyNoShows = (monthlyBookings * noShowRate) / 100;
    const annualNoShows = monthlyNoShows * 12;
    const annualLostRevenue = annualNoShows * avgBookingValue;

    // Time costs
    const annualStaffHours = staffHours * 52;
    const hourlyRate = 25; // Average hourly rate
    const annualStaffCost = annualStaffHours * hourlyRate;

    // Slotyfi improvements
    const noShowReduction = 0.85; // 85% reduction
    const timeReduction = 0.75; // 75% time savings

    // Savings calculations
    const revenueSaved = annualLostRevenue * noShowReduction;
    const timeSaved = annualStaffHours * timeReduction;
    const costSaved = timeSaved * hourlyRate;
    const totalSavings = revenueSaved + costSaved;

    // ROI calculation (assuming annual Slotyfi cost of $2400)
    const slotyfiCost = 2400;
    const roi = ((totalSavings - slotyfiCost) / slotyfiCost) * 100;

    setResults({
      timeSaved: Math.round(timeSaved),
      revenueSaved: Math.round(revenueSaved),
      costSaved: Math.round(costSaved),
      totalSavings: Math.round(totalSavings),
      roi: Math.round(roi),
      monthlyNoShows: Math.round(monthlyNoShows),
      annualNoShows: Math.round(annualNoShows),
    });

    setIsCalculated(true);
  };

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  const chartData = isCalculated
    ? [
        {
          name: 'Revenue Saved',
          value: results?.revenueSaved,
          color: '#10B981',
        },
        { name: 'Cost Reduction', value: results?.costSaved, color: '#3B82F6' },
      ]
    : [];

  const barData = isCalculated
    ? [
        {
          name: 'Before',
          noShows: results?.monthlyNoShows,
          revenue: inputs?.avgBookingValue * inputs?.monthlyBookings,
        },
        {
          name: 'After',
          noShows: results?.monthlyNoShows * 0.15,
          revenue: inputs?.avgBookingValue * inputs?.monthlyBookings * 1.15,
        },
      ]
    : [];

  return (
    <section ref={sectionRef} id="roi-calculator" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Calculator Form */}
            <div ref={calculatorRef} className="bg-gray-50 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <Icon
                    name="Calculator"
                    size={24}
                    color="#2563EB"
                    className="mr-3"
                  />
                  <h3 className="text-xl font-semibold text-gray-900">
                    ROI Calculator
                  </h3>
                </div>

                <Input
                  label={currentContent?.form?.monthlyBookings}
                  type="number"
                  value={inputs?.monthlyBookings}
                  onChange={(e) =>
                    handleInputChange('monthlyBookings', e?.target?.value)
                  }
                  placeholder="150"
                />

                <Input
                  label={currentContent?.form?.noShowRate}
                  type="number"
                  value={inputs?.noShowRate}
                  onChange={(e) =>
                    handleInputChange('noShowRate', e?.target?.value)
                  }
                  placeholder="25"
                  description="Typical range: 15-30%"
                />

                <Input
                  label={currentContent?.form?.avgBookingValue}
                  type="number"
                  value={inputs?.avgBookingValue}
                  onChange={(e) =>
                    handleInputChange('avgBookingValue', e?.target?.value)
                  }
                  placeholder="75"
                />

                <Input
                  label={currentContent?.form?.staffHours}
                  type="number"
                  value={inputs?.staffHours}
                  onChange={(e) =>
                    handleInputChange('staffHours', e?.target?.value)
                  }
                  placeholder="20"
                  description="Hours per week spent on manual scheduling"
                />

                <Button
                  variant="default"
                  size="lg"
                  onClick={calculateROI}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  iconName="TrendingUp"
                  iconPosition="left">
                  {currentContent?.calculate}
                </Button>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-8">
              {isCalculated && (
                <div
                  ref={resultsRef}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {currentContent?.results?.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {currentContent?.results?.subtitle}
                  </p>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {results?.timeSaved?.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">
                        {currentContent?.results?.metrics?.timeSaved}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        ${results?.revenueSaved?.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">
                        {currentContent?.results?.metrics?.revenueSaved}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        ${results?.costSaved?.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">
                        {currentContent?.results?.metrics?.costSaved}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {results?.roi}%
                      </div>
                      <div className="text-sm text-gray-600">
                        {currentContent?.results?.metrics?.roiPercent}
                      </div>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-semibold mb-4">
                        {currentContent?.results?.breakdown}
                      </h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={chartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              dataKey="value">
                              {chartData?.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry?.color}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) =>
                                `$${value?.toLocaleString()}`
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-semibold mb-4">
                        Before vs After Comparison
                      </h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={barData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar
                              dataKey="noShows"
                              fill="#EF4444"
                              name="No-Shows"
                            />
                            <Bar
                              dataKey="revenue"
                              fill="#10B981"
                              name="Revenue"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="default"
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-700 mt-6"
                    iconName="ArrowRight"
                    iconPosition="right">
                    {currentContent?.results?.getStarted}
                  </Button>
                </div>
              )}

              {!isCalculated && (
                <div className="bg-gray-100 rounded-2xl p-8 text-center">
                  <Icon
                    name="Calculator"
                    size={64}
                    color="#6B7280"
                    className="mx-auto mb-4"
                  />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Ready to Calculate?
                  </h3>
                  <p className="text-gray-600">
                    Fill in your business details to see your potential savings
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
